// Reusable sync engine: FIFO, exponential backoff, error classification,
// dependency ordering, local→server id mapping, restart-safe (state persisted in outbox).
import api from "../api";
import { outbox } from "./outbox";
import { storage } from "./storage";
import { connectivity } from "./connectivity";

const MAX_ATTEMPTS = 12;
const BASE_DELAY = 2000;
const MAX_DELAY = 5 * 60 * 1000;
let running = false;

function classify(status) {
  if (status === 0 || status === 408 || status === 429 || status === 502 || status === 503 || status === 504) return "retryable";
  if (status === 409) return "conflict";
  if ([400, 401, 403, 404, 422].includes(status)) return "permanent";
  if (status >= 500) return "retryable";
  return "permanent";
}

function backoff(attempt) {
  const d = Math.min(BASE_DELAY * 2 ** attempt, MAX_DELAY);
  return d + Math.random() * 500; // jitter; never a tight loop
}

async function idMap() { return (await storage.get("meta", "id_map")) || {}; }
async function putIdMap(m) { await storage.put("meta", "id_map", m); }

export const syncEngine = {
  onEvent: null, // (name, meta) => void  — wired to observability by gateway

  async syncAll(userKey, authOk) {
    if (running || connectivity.get() === "offline") return;
    running = true;
    this.onEvent && this.onEvent("sync_started", {});
    try {
      const map = await idMap();
      const items = await outbox.list(userKey);
      for (const op of items) {
        if (["synced", "failed_permanent", "conflict"].includes(op.state)) continue;
        if (op.next_retry_at && Date.parse(op.next_retry_at) > Date.now()) continue;
        // Dependencies: skip until prerequisites have a server id.
        const unmet = (op.depends_on || []).some((dep) => !map[dep]);
        if (unmet) continue;
        if (!authOk) { break; } // offline-auth window expired — stop issuing writes
        await this.syncOne(userKey, op, map);
      }
    } finally {
      running = false;
      this.onEvent && this.onEvent("sync_finished", {});
    }
  },

  async syncOne(userKey, op, map) {
    await outbox.update(userKey, op.operation_id, { state: "syncing", last_attempt_at: new Date().toISOString() });
    try {
      // Resolve any local-id references in payload via id map.
      const payload = { ...op.payload, operation_id: op.operation_id, site_id: op.site_id,
        recorded_at_device: op.created_at_device, device_tz_offset: op.device_tz_offset };
      const res = await api.post(op.endpoint, payload);
      const serverId = res.data?.record?.demo_id || res.data?.id || res.data?.record?.id || null;
      if (op.local_entity_id && serverId) { map[op.local_entity_id] = serverId; await putIdMap(map); }
      await outbox.update(userKey, op.operation_id, {
        state: "synced", server_id: serverId, synced_at_server: new Date().toISOString(),
        last_safe_error: null,
      });
    } catch (e) {
      const norm = e.normalized || { status: 0 };
      const kind = classify(norm.status);
      const attempt = (op.attempt_count || 0) + 1;
      const base = { attempt_count: attempt, last_safe_error: { status: norm.status, reference: norm.reference || norm.requestId || null } };
      this.onEvent && this.onEvent("sync_operation_failed", { operation_id: op.operation_id, status: norm.status, attempt, reference: norm.reference });
      if (kind === "conflict") {
        await outbox.update(userKey, op.operation_id, { ...base, state: "conflict",
          conflict: { local: op.payload, reference: norm.reference, at: new Date().toISOString() } });
        this.onEvent && this.onEvent("sync_conflict", { operation_id: op.operation_id, reference: norm.reference });
      } else if (kind === "permanent" || attempt >= MAX_ATTEMPTS) {
        await outbox.update(userKey, op.operation_id, { ...base, state: "failed_permanent" });
      } else {
        await outbox.update(userKey, op.operation_id, { ...base, state: "failed_retryable",
          next_retry_at: new Date(Date.now() + backoff(attempt)).toISOString() });
      }
    }
  },
};
